import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiCheck,FiClock,FiShield,FiUsers,FiStar,FiAward,FiZap}=FiIcons;

const Pricing=()=> {
  const features=[
    "20+ comprehensive video lessons (15-20 min each)",
    "Downloadable AI tools cheat sheets",
    "Step-by-step implementation guides",
    "Generative AI prompt template library",
    "AI automation workflows for common tasks",
    "Private GenX community access",
    "Gen X expert tips",
    "Email support from AI experts",
    "Lifetime access to all materials",
    "Downloadable resources",
    "Mobile-friendly learning platform",
    "30-day money-back guarantee"
  ];

  const bonuses=[
    "Bonus: AI Ethics & Privacy Guide",
    "Bonus: Industry-specific AI use cases",
    "Bonus: 1-hour recorded strategy session"
  ];

  const navigateToCourse=()=> {
    window.open('https://swiy.co/hYor','_blank','noopener,noreferrer');
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16">
        <motion.div 
          initial={{opacity: 0,y: 30}}
          whileInView={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
          viewport={{once: true}}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-slate-800 dark:text-white mb-6">
            Master AI in 30 Days - <span className="text-amber-500">Complete Training</span>
          </h2>
          <p className="text-2xl md:text-3xl text-slate-700 dark:text-slate-200 mb-10 font-medium leading-relaxed">
            Everything you need to become AI-proficient,designed specifically for Gen X professionals
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-lg text-slate-700 dark:text-slate-300 mb-10">
            <div className="flex items-center bg-green-100 dark:bg-green-900/30 px-5 py-3 rounded-xl">
              <SafeIcon icon={FiClock} className="w-6 h-6 mr-3 text-green-600" />
              <span className="font-bold">Instant access</span>
            </div>
            <div className="flex items-center bg-blue-100 dark:bg-blue-900/30 px-5 py-3 rounded-xl">
              <SafeIcon icon={FiShield} className="w-6 h-6 mr-3 text-blue-600" />
              <span className="font-bold">Secure payment</span>
            </div>
            <div className="flex items-center bg-purple-100 dark:bg-purple-900/30 px-5 py-3 rounded-xl">
              <SafeIcon icon={FiUsers} className="w-6 h-6 mr-3 text-purple-600" />
              <span className="font-bold">Join Fellow GenX Professionals</span>
            </div>
          </div>
        </motion.div>

        {/* Main Pricing Card */}
        <motion.div 
          initial={{opacity: 0,y: 30}}
          whileInView={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.2}}
          viewport={{once: true}}
          className="relative bg-gradient-to-br from-white via-amber-50 to-orange-50 dark:from-slate-800 dark:via-amber-900/20 dark:to-orange-900/20 rounded-3xl shadow-3xl border-4 border-amber-300 dark:border-amber-500 p-10 max-w-4xl mx-auto"
        >
          {/* Popular Badge */}
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-full text-lg font-bold flex items-center shadow-xl border-4 border-amber-300">
              <SafeIcon icon={FiStar} className="w-6 h-6 mr-3" />
              Gen X's AI Accelerator
            </span>
          </div>

          {/* Header */}
          <div className="text-center mb-10 pt-6">
            <h3 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">GenXDojo Complete</h3>
            <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 font-medium">The only AI training you'll ever need</p>
            
            <div className="mb-8">
              {/* Mobile Layout */}
              <div className="flex flex-col items-center md:hidden">
                <div className="text-6xl md:text-9xl font-bold text-slate-800 dark:text-white mb-2">
                  <span className="ml-2 font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent drop-shadow-2xl animate-pulse" style={{fontSize: '4rem',lineHeight: '1',fontWeight: 900}}>$65</span>
                </div>
                <div className="text-red-600 line-through decoration-red-600 decoration-4 text-2xl mb-2">$250</div>
                <span className="text-xl text-slate-500 dark:text-slate-400 font-semibold">one-time</span>
              </div>
              
              {/* Desktop Layout */}
              <div className="hidden md:flex items-center justify-center">
                <span className="text-7xl md:text-9xl font-bold text-slate-800 dark:text-white relative inline-block">
                  <span className="text-red-600 line-through decoration-red-600 decoration-6 text-5xl md:text-6xl ml-4">$250</span>
                  <span className="ml-4 font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent drop-shadow-2xl animate-pulse" style={{fontSize: '6rem',lineHeight: '1',fontWeight: 900}}>$65</span>
                </span>
                <span className="text-2xl text-slate-500 dark:text-slate-400 ml-4 font-semibold self-end">one-time</span>
              </div>
              
              <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-6 py-3 rounded-xl text-xl font-bold inline-block mt-4">
                <SafeIcon icon={FiZap} className="w-6 h-6 inline mr-2" />
                Save $185+ vs. buying similar courses separately
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {features.map((feature,index)=> (
              <motion.div 
                key={index}
                initial={{opacity: 0,x: -20}}
                whileInView={{opacity: 1,x: 0}}
                transition={{duration: 0.4,delay: index * 0.05}}
                viewport={{once: true}}
                className="flex items-start bg-white dark:bg-slate-700 p-4 rounded-xl"
              >
                <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-1" />
                <span className="text-slate-700 dark:text-slate-300 text-lg font-medium">{feature}</span>
              </motion.div>
            ))}
          </div>

          {/* Bonus Section */}
          <div className="bg-gradient-to-r from-green-100 to-blue-100 dark:from-green-900/30 dark:to-blue-900/30 p-8 rounded-2xl border-4 border-green-300 dark:border-green-600 mb-10">
            <h4 className="font-bold text-2xl text-slate-800 dark:text-white mb-6 flex items-center">
              <SafeIcon icon={FiAward} className="w-8 h-8 text-amber-500 mr-3" />
              Exclusive Bonuses Included (Value: $150)
            </h4>
            <div className="space-y-4">
              {bonuses.map((bonus,index)=> (
                <div key={index} className="flex items-center bg-white dark:bg-slate-700 p-4 rounded-xl">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-500 mr-4 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300 text-lg font-semibold">{bonus}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <motion.button 
            onClick={navigateToCourse}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-8 rounded-2xl text-4xl font-black transition-all duration-200 shadow-2xl hover:shadow-3xl border-4 border-amber-300 hover:border-amber-400"
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
          >
            {/* Mobile Layout */}
            <div className="flex flex-col items-center md:hidden">
              <span>Get Instant Access</span>
              <span className="font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent drop-shadow-lg animate-pulse" style={{fontSize: '2.5rem',fontWeight: 900}}>$65</span>
              <span className="text-red-600 line-through decoration-red-600 decoration-4 text-lg">$250</span>
            </div>
            
            {/* Desktop Layout */}
            <div className="hidden md:flex items-center justify-center">
              <span>Get Instant Access - </span>
              <span className="relative inline-block">
                <span className="text-red-600 line-through decoration-red-600 decoration-6 text-2xl ml-3">$250</span>
                <span className="ml-3 font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent drop-shadow-lg animate-pulse" style={{fontSize: '4rem',fontWeight: 900}}>$65</span>
              </span>
            </div>
          </motion.button>

          <div className="text-center mt-8 space-y-4">
            <p className="text-slate-600 dark:text-slate-400 text-lg font-semibold">
              <SafeIcon icon={FiShield} className="w-6 h-6 inline mr-2 text-green-500" />
              30-day money-back guarantee
            </p>
            <p className="text-slate-500 dark:text-slate-500 text-base">Secure payment • Cancel anytime • Instant access</p>
          </div>
        </motion.div>

        {/* Value Proposition */}
        <motion.div 
          initial={{opacity: 0}}
          whileInView={{opacity: 1}}
          transition={{duration: 0.6,delay: 0.4}}
          viewport={{once: true}}
          className="text-center mt-16 bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 p-10 rounded-2xl border-4 border-slate-300 dark:border-slate-500"
        >
          <h3 className="text-3xl font-bold text-slate-800 dark:text-white mb-8">Why This Investment Makes Sense</h3>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border-2 border-amber-200 dark:border-amber-600">
              <div className="text-4xl font-bold text-amber-500 mb-3">5+ Hours</div>
              <p className="text-slate-600 dark:text-slate-400 text-lg font-medium">Saved per week using AI tools</p>
            </div>
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border-2 border-green-200 dark:border-green-600">
              <div className="text-4xl font-bold text-green-500 mb-3">$50/hr</div>
              <p className="text-slate-600 dark:text-slate-400 text-lg font-medium">Value of time saved monthly</p>
            </div>
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border-2 border-blue-200 dark:border-blue-600">
              <div className="text-4xl font-bold text-blue-500 mb-3">1 Month</div>
              <p className="text-slate-600 dark:text-slate-400 text-lg font-medium">To pay for itself completely</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;